import { createFlow } from '@wix/gg-components/orchestrator';
import { StepId, ActionId } from 'config/steps';
import { StepParams, StepRenderPart } from 'types/steps';
import { WelcomeStep } from 'steps/welcome/WelcomeStep';
import { CategoryContent } from 'steps/category/CategoryContent';
import { CategoryPreview } from 'steps/category/CategoryPreview';
import { StoreNameContent } from 'steps/storeName/StoreNameContent';
import { StoreNamePreview } from 'steps/storeName/StoreNamePreview';
import { LaunchStoreContent } from 'steps/launchStore/LaunchStoreContent';
import { LaunchStorePreview } from 'steps/launchStore/LaunchStorePreview';
import { SitePreviewContent } from 'steps/sitePreview/SitePreviewContent';
import { SitePreviewPreview } from 'steps/sitePreview/SitePreviewPreview';
import { welcomeNextAction, categoryNextAction, templateNextAction, storeNameNextAction } from '../../actions';
import { SetupStoreContent } from 'steps/setupStore/SetupStoreContent';
import { SetupStorePreview } from 'steps/setupStore/SetupStorePreview';
import { getInitialCategory, getInitialStoreName, getInitialThemeKey } from 'config/client';
import { getProviderConfig, getSubIdConfig } from 'config/providers';
import { ThemeContent } from 'steps/theme/ThemeContent';
import { ThemePreview } from 'steps/theme/ThemePreview';
import { SitePreviewPremium } from 'steps/sitePreview/SitePreviewPremium';
import { useIsMobile } from '@wix/gg-common-lib/hooks';
import { shouldShowPremiumHalfOff } from 'utils/premium';

export function useFlowBuilder() {
  const flow = createFlow<StepParams, StepRenderPart>();
  const providerConfig = getProviderConfig();
  const { skipWelcomeStep } = providerConfig;
  const skipCategory = !!getInitialCategory();
  const skipTheme = !!getInitialThemeKey();
  const skipStoreName = !!getInitialStoreName();
  const isMobile = useIsMobile();

  const showWelcome = !skipCategory && (!skipWelcomeStep || !!getSubIdConfig()?.showWelcome);

  if (showWelcome) {
    flow.step(StepId.Welcome, WelcomeStep, { fullScreen: true, countInStepper: false });
    flow.action(ActionId.WelcomeNext, welcomeNextAction);
  }
  if (!skipCategory) {
    flow.step(StepId.Category, { main: CategoryContent, preview: CategoryPreview }, { splitRatio: 30 });
    flow.action(ActionId.CategoryNext, categoryNextAction);
  }
  if (!skipTheme) {
    flow.step(StepId.Theme, { main: ThemeContent, preview: ThemePreview }, { splitRatio: 30 });
    flow.action(ActionId.ThemeNext, templateNextAction);
  }
  flow.step(
    StepId.SetupStore,
    { main: SetupStoreContent, preview: SetupStorePreview },
    { fullScreen: true, countInStepper: false },
  );
  if (!skipStoreName) {
    flow.step(
      StepId.StoreName,
      { main: StoreNameContent, preview: StoreNamePreview },
      { splitRatio: 30, checkpoint: true },
    );
    flow.action(ActionId.StoreNameNext, storeNameNextAction);
  }
  flow.step(
    StepId.LaunchStore,
    { main: LaunchStoreContent, preview: LaunchStorePreview },
    { splitRatio: 30, countInStepper: false },
  );

  if (shouldShowPremiumHalfOff()) {
    const component = isMobile ? SitePreviewPremium : { preview: SitePreviewPremium };
    flow.step(StepId.SitePreview, component, { countInStepper: false, splitRatio: 0 });
  } else {
    flow.step(
      StepId.SitePreview,
      { main: SitePreviewContent, preview: SitePreviewPreview },
      { fullScreen: true, checkpoint: true, mobileOnlyFooter: true, countInStepper: false, splitRatio: 40 },
    );
  }

  return flow.build();
}
